import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notify } from '../../components/common/AppToast';
import MasterLayout from '../../masterLayout/MasterLayout';
import DepotHistoryPanel from '../../components/depots/DepotHistoryPanel';
import DepotUploadForm from '../../components/depots/DepotUploadForm';
import {
  DEFAULT_DESTINATION,
  HISTORY_ALL_LIMIT,
  HISTORY_RECENT_LIMIT,
  MAX_BATCH_BYTES,
  MAX_FILES,
  createPendingFileItem,
  createPendingNotesItem,
  currentMonthIso,
  depotAwaitingValidation,
  fileCount,
  filesTotalBytes,
  getDriveClientValue,
  matchDepositorPersonneId,
  personneIdFromCommercialId,
  splitPersonneSelection,
  todayIso,
  validateDroppedFile,
} from '../../components/depots/depotUtils';
import depotService from '../../services/depotService';
import authService from '../../services/authService';
import { setDepotPendingCount } from '../../utils/depotPendingCountStore';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/depot.scss';

export default function DepotPage() {
  const { ask, modal } = useAppConfirm();
  const fileInputRef = useRef(null);
  const formCardRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [creatingClient, setCreatingClient] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [clientFolderId, setClientFolderId] = useState('');
  const [commercialId, setCommercialId] = useState('');
  const [commercialSource, setCommercialSource] = useState(null);
  const [personnes, setPersonnes] = useState([]);
  const [typeRdv, setTypeRdv] = useState('rdv_commercial');
  const [dateRdv, setDateRdv] = useState(todayIso);
  const [dateDocument, setDateDocument] = useState(currentMonthIso);
  const [extractionRequise, setExtractionRequise] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadIndex, setUploadIndex] = useState(0);
  const [depots, setDepots] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [historyTab, setHistoryTab] = useState('recents');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [filterCommercial, setFilterCommercial] = useState('');
  const [historyFiltersOpen, setHistoryFiltersOpen] = useState(false);
  const [relancingId, setRelancingId] = useState(null);
  const [validatingId, setValidatingId] = useState(null);
  const [bebedcId, setBebedcId] = useState(null);
  const [bebedcPendingIds, setBebedcPendingIds] = useState(() => new Set());
  const [deletingId, setDeletingId] = useState(null);
  const canSeeTeam = authService.canSeeTeamDepots();

  const pendingFilesBytes = filesTotalBytes(pendingItems);
  const pendingFileCount = fileCount(pendingItems);

  const hasEnCours = useMemo(
    () => depots.some((depot) => depot.statut === 'en_cours') || bebedcPendingIds.size > 0,
    [depots, bebedcPendingIds],
  );

  const pendingReviewCount = useMemo(
    () => depots.filter((depot) => depotAwaitingValidation(depot)).length,
    [depots],
  );

  const filteredDepots = useMemo(() => {
    const query = filterQuery.trim().toLowerCase();
    return depots.filter((depot) => {
      if (filterType && depot.type_depot !== filterType) {
        return false;
      }
      if (filterCommercial && String(depot.personne_id || '') !== String(filterCommercial)) {
        return false;
      }
      if (filterStatut === 'a_relire' && !depotAwaitingValidation(depot)) {
        return false;
      }
      if (filterStatut === 'en_cours' && (depot.statut !== 'en_cours' || depotAwaitingValidation(depot))) {
        return false;
      }
      if (filterStatut === 'termine' && (depot.statut !== 'termine' || depotAwaitingValidation(depot))) {
        return false;
      }
      if (filterStatut === 'erreur' && depot.statut !== 'erreur') {
        return false;
      }
      if (query) {
        const haystack = [
          depot.client_nom,
          depot.fichier_depose,
          depot.nom_fichier_original,
          depot.objet,
          depot.commercial_nom,
          depot.depose_par,
        ].join(' ').toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [depots, filterStatut, filterType, filterQuery, filterCommercial]);

  const visibleDepots = historyTab === 'tous'
    ? filteredDepots
    : filteredDepots.slice(0, HISTORY_RECENT_LIMIT);

  const hasActiveFilters = Boolean(filterStatut || filterType || filterQuery || filterCommercial);

  const overallProgress = useMemo(() => {
    if (!uploading || pendingItems.length === 0) return null;
    const sum = pendingItems.reduce((acc, item) => acc + (item.progress || 0), 0);
    return Math.round(sum / pendingItems.length);
  }, [uploading, pendingItems]);

  const loadClients = useCallback(async ({ rafraichir = false, silent = false } = {}) => {
    if (!silent) {
      setClientsLoading(true);
    }
    try {
      const data = await depotService.listClientsDrive({ rafraichir });
      const list = data.clients;
      setClients(list);
      if (rafraichir) {
        setClientFolderId((current) => (
          current && list.some((client) => getDriveClientValue(client) === current) ? current : ''
        ));
        setPendingItems((current) => current.map((item) => {
          if (!item.clientFolderId) return item;
          const stillExists = list.some((client) => getDriveClientValue(client) === item.clientFolderId);
          return stillExists ? item : { ...item, clientFolderId: '', clientFromFilename: false };
        }));
      }
      return data;
    } catch (err) {
      if (!silent) {
        notify.error(err.message || 'Impossible de charger la liste des clients');
      }
      return { clients: [], from_cache: false, stale: false };
    } finally {
      if (!silent) {
        setClientsLoading(false);
      }
    }
  }, []);

  const loadDestinations = useCallback(async () => {
    try {
      const data = await depotService.listDestinations();
      setDestinations(data);
    } catch (err) {
      notify.error(err.message || 'Impossible de charger les dossiers de destination');
    }
  }, []);

  const loadPersonnes = useCallback(async () => {
    try {
      const data = await depotService.listPersonnes();
      const list = Array.isArray(data) ? data : [];
      setPersonnes(list);
      const depositorId = matchDepositorPersonneId(list);
      setCommercialId((current) => current || depositorId);
      if (depositorId) {
        setCommercialSource((current) => current || 'deposant');
      }
    } catch (err) {
      notify.error(err.message || 'Impossible de charger la liste des personnes');
    }
  }, []);

  const loadDepots = useCallback(async () => {
    setListError(null);
    try {
      const data = await depotService.listDepots({ tous: canSeeTeam && showAll, limit: HISTORY_ALL_LIMIT });
      setDepots(Array.isArray(data.depots) ? data.depots : []);
      try {
        const countData = await depotService.getPendingCount();
        const total = Number(countData?.total);
        setDepotPendingCount(Number.isFinite(total) ? total : 0);
      } catch {
        // Le badge menu se rafraîchira au prochain changement de route.
      }
    } catch (err) {
      setListError(err.message || 'Impossible de charger les dépôts');
    } finally {
      setListLoading(false);
    }
  }, [showAll, canSeeTeam]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapClients = async () => {
      const first = await loadClients({ rafraichir: false });
      if (cancelled) return;
      // Cache frais (< 2 min) : pas de nouvel appel Drive. Sinon refresh silencieux.
      if (first.from_cache && first.stale) {
        await loadClients({ rafraichir: true, silent: true });
      }
    };

    void bootstrapClients();
    loadDestinations();
    loadPersonnes();

    return () => {
      cancelled = true;
    };
  }, [loadClients, loadDestinations, loadPersonnes]);

  useEffect(() => {
    setListLoading(true);
    loadDepots();
  }, [loadDepots]);

  useEffect(() => {
    if (!hasEnCours) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      loadDepots();
    }, depotService.POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [hasEnCours, loadDepots]);

  useEffect(() => {
    setBebedcPendingIds((current) => {
      if (current.size === 0) {
        return current;
      }
      const next = new Set();
      current.forEach((id) => {
        const depot = depots.find((item) => item.id === id);
        if (depot && !depot.bebedc_url && depot.statut !== 'erreur') {
          next.add(id);
        }
      });
      if (next.size === current.size) {
        let unchanged = true;
        current.forEach((id) => {
          if (!next.has(id)) unchanged = false;
        });
        if (unchanged) return current;
      }
      return next;
    });
  }, [depots]);

  useEffect(() => {
    let cancelled = false;

    const applySuggestion = async () => {
      const depositorId = matchDepositorPersonneId(personnes);
      if (!clientFolderId) {
        setCommercialId((current) => current || depositorId);
        setCommercialSource((current) => current || (depositorId ? 'deposant' : null));
        return;
      }
      try {
        const suggestion = await depotService.suggestCommercial(clientFolderId);
        if (cancelled) return;
        const suggestedId = personneIdFromCommercialId(personnes, suggestion.commercial_id);
        if (suggestedId) {
          setCommercialId(suggestedId);
          setCommercialSource(suggestion.source);
          return;
        }
        setCommercialId(depositorId);
        setCommercialSource(depositorId ? 'deposant' : null);
      } catch {
        if (cancelled) return;
        setCommercialId(depositorId);
        setCommercialSource(depositorId ? 'deposant' : null);
      }
    };

    applySuggestion();

    return () => {
      cancelled = true;
    };
  }, [clientFolderId, personnes]);

  const focusForm = () => {
    formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    fileInputRef.current?.click();
  };

  const applyCommercialSuggestion = useCallback(async (folderId, itemIds) => {
    if (!itemIds.length) return;
    const depositorId = matchDepositorPersonneId(personnes);
    let nextId = depositorId;
    let nextSource = depositorId ? 'deposant' : null;
    if (folderId) {
      try {
        const suggestion = await depotService.suggestCommercial(folderId);
        const suggestedId = personneIdFromCommercialId(personnes, suggestion.commercial_id);
        if (suggestedId) {
          nextId = suggestedId;
          nextSource = suggestion.source;
        }
      } catch {
        // garder le profil du déposant
      }
    }
    if (!nextId) return;
    setPendingItems((current) => current.map((item) => (
      itemIds.includes(item.id)
        ? { ...item, commercialId: nextId, commercialSource: nextSource }
        : item
    )));
  }, [personnes]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;

    const defaults = {
      dateRdv,
      dateDocument,
      clientFolderId,
      clients,
      typeRdv,
      commercialId,
      commercialSource,
    };
    const nextItems = [];
    const errors = [];

    for (const file of incoming) {
      const error = validateDroppedFile(file);
      if (error) {
        errors.push(error);
        continue;
      }
      nextItems.push(createPendingFileItem(file, defaults));
    }

    if (errors.length > 0) {
      notify.error(errors.slice(0, 2).join(' '));
    }
    if (nextItems.length === 0) return;

    if (!clientFolderId) {
      const detectedClient = nextItems.find((item) => item.clientFromFilename && item.clientFolderId);
      if (detectedClient) {
        setClientFolderId(detectedClient.clientFolderId);
      }
    }

    setPendingItems((current) => {
      const merged = [...current];
      const skippedForCumul = [];

      for (const item of nextItems) {
        const duplicate = merged.some(
          (existing) => existing.file
            && existing.file.name === item.file.name
            && existing.file.size === item.file.size,
        );
        if (duplicate) {
          continue;
        }
        const nextFileCount = fileCount(merged) + 1;
        if (nextFileCount >= 2 && filesTotalBytes(merged) + item.file.size > MAX_BATCH_BYTES) {
          skippedForCumul.push(item.file.name);
          continue;
        }
        merged.push(item);
      }

      if (skippedForCumul.length > 0) {
        notify.error(
          skippedForCumul.length === 1
            ? `« ${skippedForCumul[0]} » refusé : le cumul des fichiers dépasse 10 Go.`
            : `${skippedForCumul.length} fichiers refusés : le cumul dépasse 10 Go.`,
        );
      }
      if (merged.length > MAX_FILES) {
        notify.error(`Maximum ${MAX_FILES} éléments par envoi.`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });

    const folders = [...new Set(nextItems.map((item) => item.clientFolderId).filter(Boolean))];
    folders.forEach((folder) => {
      const ids = nextItems.filter((item) => item.clientFolderId === folder).map((item) => item.id);
      void applyCommercialSuggestion(folder, ids);
    });
  };

  const addNotesItem = () => {
    setPendingItems((current) => {
      if (current.length >= MAX_FILES) {
        notify.error(`Maximum ${MAX_FILES} éléments par envoi.`);
        return current;
      }
      return [...current, createPendingNotesItem({
        dateDocument,
        clientFolderId,
        commercialId,
        commercialSource,
      })];
    });
  };

  const updatePendingItem = (id, patch) => {
    setPendingItems((current) => current.map((item) => (
      item.id === id ? { ...item, ...patch } : item
    )));
  };

  const removePendingItem = (id) => {
    setPendingItems((current) => current.filter((item) => item.id !== id));
  };

  const applyDefaultClientToAll = () => {
    if (!clientFolderId) {
      notify.error('Choisissez d’abord un client en haut du formulaire.');
      return;
    }
    setPendingItems((current) => current.map((item) => ({
      ...item,
      clientFolderId,
      clientFromFilename: false,
      commercialId: commercialId || item.commercialId,
      commercialSource: commercialId ? commercialSource : item.commercialSource,
    })));
  };

  const applyDefaultCommercialToAll = () => {
    if (!commercialId) {
      notify.error('Choisissez d’abord qui a fait le rendez-vous.');
      return;
    }
    setPendingItems((current) => current.map((item) => ({
      ...item,
      commercialId,
      commercialSource: commercialSource || 'manuel',
    })));
  };

  const resolveItemClientFolderId = (item) => item.clientFolderId || clientFolderId;

  const resolveItemCommercialId = (item) => item.commercialId || commercialId;

  const createDriveClient = useCallback(async (nom, { itemId = null } = {}) => {
    const label = String(nom || '').trim();
    if (label.length < 2) {
      throw new Error('Indiquez un nom de client (au moins 2 caractères).');
    }

    setCreatingClient(true);
    try {
      const created = await depotService.createClientDrive(label);
      const refreshed = await depotService.listClientsDrive({ rafraichir: true });
      setClients(refreshed.clients);
      const folderId = created?.folder_id || '';
      if (!folderId) {
        throw new Error('Dossier créé sans identifiant Drive.');
      }

      if (itemId) {
        updatePendingItem(itemId, {
          clientFolderId: folderId,
          clientFromFilename: false,
        });
        void applyCommercialSuggestion(folderId, [itemId]);
      } else {
        setClientFolderId(folderId);
      }

      notify.success(`Dossier « ${created.nom || label} » créé sur Drive`);
      return created;
    } catch (err) {
      notify.error(err.message || 'Impossible de créer le dossier client sur Drive');
      throw err;
    } finally {
      setCreatingClient(false);
    }
  }, [applyCommercialSuggestion]);

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    addFiles(event.dataTransfer?.files);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (pendingItems.length === 0) {
      notify.error('Ajoutez un fichier ou des notes, puis cliquez sur Envoyer.');
      return;
    }
    if (pendingItems.some((item) => !resolveItemClientFolderId(item))) {
      notify.error(
        pendingItems.length === 1
          ? 'Choisissez le client.'
          : 'Chaque fichier doit avoir un client.',
      );
      return;
    }
    if (pendingItems.some((item) => !resolveItemCommercialId(item))) {
      notify.error(
        pendingItems.length === 1
          ? 'Indiquez qui a fait le rendez-vous.'
          : 'Chaque fichier doit indiquer qui a fait le rendez-vous.',
      );
      return;
    }
    if (pendingItems.some((item) => item.typeDepot === 'document' && !item.objet.trim())) {
      notify.error('Indiquez l’objet de chaque document (contrat, bilan…).');
      return;
    }
    if (fileCount(pendingItems) >= 2 && filesTotalBytes(pendingItems) > MAX_BATCH_BYTES) {
      notify.error('Le cumul des fichiers dépasse 10 Go.');
      return;
    }

    setUploading(true);
    setUploadIndex(0);
    let successCount = 0;
    let errorCount = 0;

    for (let index = 0; index < pendingItems.length; index += 1) {
      const item = pendingItems[index];
      setUploadIndex(index + 1);
      updatePendingItem(item.id, { status: 'uploading', progress: 0, error: null });

      const formData = new FormData();
      formData.append('type_depot', item.typeDepot);
      formData.append('client_folder_id', resolveItemClientFolderId(item));
      const personne = splitPersonneSelection(personnes, resolveItemCommercialId(item));
      if (personne.userId) {
        formData.append('user_id', personne.userId);
      }
      if (personne.commercialId) {
        formData.append('commercial_id', personne.commercialId);
      }
      formData.append('validation_requise', '1');

      if (item.typeDepot === 'notes') {
        formData.append('texte', item.texte);
        formData.append('destination_dossier', DEFAULT_DESTINATION);
        if (item.date) {
          formData.append('date_rdv', item.date);
        }
      } else {
        formData.append('fichier', item.file);
        if (item.file?.name) {
          formData.append('nom_fichier_original', item.file.name);
        }
        if (item.typeDepot === 'document') {
          formData.append('objet', item.objet.trim());
          formData.append('destination_dossier', DEFAULT_DESTINATION);
          formData.append('date_rdv', item.date || dateDocument || currentMonthIso());
          formData.append('extraction_requise', extractionRequise ? '1' : '0');
        } else {
          formData.append('date_rdv', item.date || dateRdv);
          formData.append('type_rdv', item.typeRdv || typeRdv);
        }
      }

      try {
        await depotService.uploadDepot(formData, (progress) => {
          updatePendingItem(item.id, { progress: item.typeDepot === 'notes' ? 100 : progress });
        });
        updatePendingItem(item.id, { status: 'done', progress: 100 });
        successCount += 1;
      } catch (err) {
        errorCount += 1;
        updatePendingItem(item.id, {
          status: 'error',
          progress: 0,
          error: err.message || 'Échec de l’envoi',
        });
      }
    }

    await loadDepots();
    setUploading(false);

    if (errorCount === 0) {
      setPendingItems([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      notify.success(
        successCount === 1
          ? 'Dépôt envoyé'
          : `${successCount} dépôts envoyés`,
      );
    } else if (successCount > 0) {
      notify.error(`${successCount} envoyé(s), ${errorCount} en échec — corrigez puis renvoyez.`);
      setPendingItems((current) => current.filter((item) => item.status === 'error'));
    } else {
      notify.error('Aucun dépôt n’a pu être envoyé.');
    }
  };

  const handleRelancer = async (depotId) => {
    setRelancingId(depotId);
    try {
      await depotService.relancer(depotId);
      notify.success('Traitement relancé');
      await loadDepots();
    } catch (err) {
      notify.error(err.message || 'Impossible de relancer');
    } finally {
      setRelancingId(null);
    }
  };

  const handleValider = async (depotId) => {
    setValidatingId(depotId);
    try {
      await depotService.valider(depotId);
      notify.success('Document validé — finalisation en cours');
      await loadDepots();
    } catch (err) {
      notify.error(err.message || 'Impossible de valider');
    } finally {
      setValidatingId(null);
    }
  };

  const handleBebedc = async (depotId) => {
    const depot = depots.find((item) => item.id === depotId);
    const alsoValidates = depotAwaitingValidation(depot);
    setBebedcId(depotId);
    setBebedcPendingIds((current) => new Set(current).add(depotId));
    try {
      await depotService.lancerBebedc(depotId);
      notify.success(alsoValidates
        ? 'Validation et analyse BEBEDC lancées'
        : 'Analyse BEBEDC lancée');
      await loadDepots();
    } catch (err) {
      setBebedcPendingIds((current) => {
        const next = new Set(current);
        next.delete(depotId);
        return next;
      });
      notify.error(err.message || 'Impossible de lancer BEBEDC');
    } finally {
      setBebedcId(null);
    }
  };

  const handleDelete = async (depot) => {
    const label = depot.objet || depot.client_nom;
    const ok = await ask({
      title: 'Retirer de l’historique ?',
      message: `Retirer « ${label} » de votre historique ?\n(Le fichier sur Drive n’est pas supprimé.)`,
      confirmLabel: 'Retirer',
      confirmVariant: 'danger',
    });
    if (!ok) {
      return;
    }
    setDeletingId(depot.id);
    try {
      await depotService.deleteDepot(depot.id);
      notify.success('Retiré de l’historique');
      await loadDepots();
    } catch (err) {
      notify.error(err.message || 'Suppression impossible');
    } finally {
      setDeletingId(null);
    }
  };

  const showPerFileClient = pendingItems.length > 1;
  const itemsHaveClient = pendingItems.every((item) => Boolean(item.clientFolderId || clientFolderId));
  const itemsHaveCommercial = pendingItems.every((item) => Boolean(item.commercialId || commercialId));
  const canSubmit = Boolean(pendingItems.length > 0 && itemsHaveClient && itemsHaveCommercial && !uploading);
  const historyExtraFiltersOpen = historyFiltersOpen
    || Boolean(filterStatut || filterType || filterCommercial);
  const submitHint = !pendingItems.length
    ? 'Ajoutez un fichier ou des notes, puis cliquez sur Envoyer.'
    : !itemsHaveClient
      ? 'Choisissez le client avant d’envoyer.'
      : !itemsHaveCommercial
        ? 'Indiquez qui a fait le rendez-vous avant d’envoyer.'
        : '';

  const openFilePicker = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  const clearHistoryFilters = () => {
    setFilterStatut('');
    setFilterType('');
    setFilterQuery('');
    setFilterCommercial('');
    setHistoryFiltersOpen(false);
  };

  return (
    <MasterLayout>
      <div className="depot-page">
        <div className="row gy-4 align-items-start">
          <div className="col-12 depot-page__form">
            <DepotUploadForm
              formCardRef={formCardRef}
              fileInputRef={fileInputRef}
              clients={clients}
              clientsLoading={clientsLoading}
              creatingClient={creatingClient}
              clientFolderId={clientFolderId}
              onClientFolderIdChange={setClientFolderId}
              onRefreshClients={loadClients}
              onCreateClient={createDriveClient}
              personnes={personnes}
              commercialId={commercialId}
              onCommercialChange={(next) => {
                setCommercialId(next);
                setCommercialSource(next ? 'manuel' : null);
              }}
              pendingItems={pendingItems}
              uploading={uploading}
              uploadIndex={uploadIndex}
              overallProgress={overallProgress}
              dragActive={dragActive}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onOpenFilePicker={openFilePicker}
              onFileInputChange={(event) => {
                addFiles(event.target.files);
                event.target.value = '';
              }}
              onAddNotes={addNotesItem}
              onUpdatePendingItem={updatePendingItem}
              onRemovePendingItem={removePendingItem}
              onApplyDefaultClientToAll={applyDefaultClientToAll}
              onApplyDefaultCommercialToAll={applyDefaultCommercialToAll}
              onApplyCommercialSuggestion={applyCommercialSuggestion}
              typeRdv={typeRdv}
              extractionRequise={extractionRequise}
              onExtractionChange={setExtractionRequise}
              showPerFileClient={showPerFileClient}
              pendingFileCount={pendingFileCount}
              pendingFilesBytes={pendingFilesBytes}
              canSubmit={canSubmit}
              submitHint={submitHint}
              onSubmit={handleSubmit}
            />
          </div>

          <div className="col-12 depot-page__history">
            <DepotHistoryPanel
              showAll={showAll}
              canSeeTeam={canSeeTeam}
              onShowAllChange={setShowAll}
              pendingReviewCount={pendingReviewCount}
              filterQuery={filterQuery}
              onFilterQueryChange={setFilterQuery}
              historyTab={historyTab}
              onHistoryTabChange={setHistoryTab}
              historyExtraFiltersOpen={historyExtraFiltersOpen}
              onToggleFilters={() => setHistoryFiltersOpen((open) => !open)}
              filterStatut={filterStatut}
              onFilterStatutChange={setFilterStatut}
              filterType={filterType}
              onFilterTypeChange={setFilterType}
              filterCommercial={filterCommercial}
              onFilterCommercialChange={setFilterCommercial}
              personnes={personnes}
              listLoading={listLoading}
              listError={listError}
              depots={depots}
              visibleDepots={visibleDepots}
              filteredDepots={filteredDepots}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearHistoryFilters}
              onRetryLoad={loadDepots}
              onFocusForm={focusForm}
              destinations={destinations}
              onRelancer={handleRelancer}
              onValider={handleValider}
              onBebedc={handleBebedc}
              onDelete={handleDelete}
              relancingId={relancingId}
              validatingId={validatingId}
              bebedcId={bebedcId}
              deletingId={deletingId}
              uploading={uploading}
              bebedcPendingIds={bebedcPendingIds}
            />
          </div>
        </div>
      </div>
      {modal}
    </MasterLayout>
  );
}
